if (!customElements.get('wlsn-form')) {
  class WLSNForm extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        loading: 'wlsn-form--loading',
        success: 'wlsn-form--success',
        error: 'wlsn-form--error',
        hidden: 'hidden',
        fieldError: 'wlsn-form-field--error',
        formFields: 'wlsn-form__fields',
        successMessage: 'wlsn-form__success-wrapper',
        submitButton: 'wlsn-form__submit',
        errorContainer: 'wlsn-form-field__error',
      };

      this.onSubmit = this._handleSubmit.bind(this);

      this.formMode = {
        standalone: 'standalone',
        validateOnly: 'validate-only',
      };
    }

    /**
     * Mode:
     * - "standalone" (default) → handles submit (_sendForm itd.)
     * - "validate-only" → only validates, does not handle submit
     * @returns {string} - Mode
     */
    get mode() {
      return this.getAttribute('data-mode') || this.formMode.validateOnly;
    }

    /**
     * @returns {Object}
     * @description Get all DOM elements used in the component
     */
    get elements() {
      return {
        form: this.querySelector('form'),
        formFields: this.querySelector(`.${this.classes.formFields}`),
        successMessage: this.querySelector(`.${this.classes.successMessage}`),
        submitButton: this.querySelector(`.${this.classes.submitButton}`),
        fields: this.querySelectorAll('input:not([type="hidden"]), textarea, select'),
        errorContainers: this.querySelectorAll(`.${this.classes.errorContainer}`),
      };
    }

    /**
     * @description Initialize form when component is added to DOM
     */
    connectedCallback() {
      const { form, fields, successMessage } = this.elements;
      if (!form) return;

      // Only in "standalone" mode intercept submit
      if (this.mode === this.formMode.standalone) {
        form.addEventListener('submit', this.onSubmit);
      } else {
        // Validate-only mode: native form posts and the page reloads. Show
        // the loading state so a spinner can appear between click and navigation.
        form.addEventListener('submit', (e) => {
          // Stash submitted values so the post-reload thanks block can hydrate
          // its [full_name]/[email] tokens. The thanks block only renders on
          // the success state, so it can't capture the submit itself.
          // Storage key is shared with wlsn-form-contact-thanks.js.
          try {
            const formData = new FormData(e.target);
            const values = {
              'contact[name]': formData.get('contact[name]') || '',
              'contact[email]': formData.get('contact[email]') || '',
            };
            sessionStorage.setItem('wlsn-form-contact-thanks:values', JSON.stringify(values));
          } catch (err) {
            // sessionStorage unavailable (e.g. private mode) — tokens just won't hydrate
          }

          this.classList.add(this.classes.loading);
          const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.setAttribute('aria-busy', 'true');
          }
        });
      }

      // Blur + input validation always
      fields.forEach((field) => {
        field.addEventListener('blur', () => this._validateField(field));
        field.addEventListener('input', () => this._clearFieldError(field));
      });

      // Page-reload success state (native form): if the success wrapper is
      // already visible on connect and an auto-reset delay is configured,
      // schedule the revert to the form.
      const autoResetDelay = parseInt(this.getAttribute('data-success-reset-delay'), 10);
      if (autoResetDelay > 0 && successMessage && !successMessage.classList.contains(this.classes.hidden)) {
        this._scheduleSuccessReset(autoResetDelay);
      }
    }

    /**
     * @description Cleanup when component is removed from DOM
     */
    disconnectedCallback() {
      const { form, fields } = this.elements;
      if (!form) return;

      if (this.mode === this.formMode.standalone) {
        form.removeEventListener('submit', this.onSubmit);
      }

      fields.forEach((field) => {
        field.removeEventListener('blur', this._validateField);
        field.removeEventListener('input', this._clearFieldError);
      });
    }

    /**
     * Public API for other components (e.g. wlsn-product-form)
     * @returns {boolean}
     */
    validate() {
      this._clearAllErrors();

      let isValid = true;
      this.elements.fields.forEach((field) => {
        if (!this._validateField(field)) {
          isValid = false;
        }
      });

      return isValid;
    }

    /**
     * Handle form submission
     * @param {Event} e - Form submit event
     * @returns {void}
     */
    _handleSubmit(e) {
      // This submit handler is used ONLY in "standalone" mode
      e.preventDefault();

      if (!this.validate()) {
        e.stopPropagation();
        return;
      }

      // Show loading state
      this.classList.add(this.classes.loading);
      if (this.elements.submitButton) {
        this.elements.submitButton.disabled = true;
        this.elements.submitButton.setAttribute('aria-busy', 'true');
      }

      // Collect form data
      const formData = new FormData(this.elements.form);

      // Send form
      this._sendForm(formData);
    }

    /**
     * Validate a single field
     * @param {HTMLElement} field - Input field to validate
     * @returns {boolean} - Whether field is valid
     */
    _validateField(field) {
      const fieldWrapper = field.closest('.wlsn-form-field');

      // Check HTML5 validity
      if (!field.checkValidity()) {
        this._showFieldError(field, field.validationMessage);
        return false;
      }

      // Custom validation for specific field types
      if (field.type === 'email' && field.value) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(field.value)) {
          this._showFieldError(field, 'Please enter a valid email address');
          return false;
        }
      }

      if (field.type === 'tel' && field.value) {
        const phonePattern = field.getAttribute('pattern');
        if (phonePattern) {
          const regex = new RegExp(phonePattern);
          if (!regex.test(field.value)) {
            this._showFieldError(field, 'Please enter a valid phone number');
            return false;
          }
        }
      }

      // Checkbox validation
      if (field.type === 'checkbox' && field.hasAttribute('required') && !field.checked) {
        this._showFieldError(field, 'This field is required');
        return false;
      }

      this._clearFieldError(field);
      return true;
    }

    /**
     * Show error message for a field
     * @param {HTMLElement} field - Input field
     * @param {string} message - Error message to display
     * @returns {void}
     */
    _showFieldError(field, message) {
      const fieldWrapper = field.closest('.wlsn-form-field');
      if (!fieldWrapper) return;

      const errorContainer = fieldWrapper.querySelector(`.${this.classes.errorContainer}`);

      fieldWrapper.classList.add(this.classes.fieldError);
      field.setAttribute('aria-invalid', 'true');

      if (errorContainer) {
        errorContainer.textContent = message;
        errorContainer.setAttribute('role', 'alert');
      }
    }

    /**
     * Clear error message for a field
     * @param {HTMLElement} field - Input field
     * @returns {void}
     */
    _clearFieldError(field) {
      const fieldWrapper = field.closest('.wlsn-form-field');
      if (!fieldWrapper) return;

      const errorContainer = fieldWrapper.querySelector(`.${this.classes.errorContainer}`);

      fieldWrapper.classList.remove(this.classes.fieldError);
      field.removeAttribute('aria-invalid');

      if (errorContainer) {
        errorContainer.textContent = '';
        errorContainer.removeAttribute('role');
      }
    }

    /**
     * Clear all error messages
     * @returns {void}
     */
    _clearAllErrors() {
      this.elements.errorContainers.forEach((container) => {
        container.textContent = '';
        container.removeAttribute('role');
      });

      this.querySelectorAll(`.${this.classes.fieldError}`).forEach((field) => {
        field.classList.remove(this.classes.fieldError);
      });

      this.elements.fields.forEach((field) => {
        field.removeAttribute('aria-invalid');
      });
    }

    /**
     * Send form data via AJAX to Shopify
     * @param {FormData} formData - Form data to send
     * @returns {Promise<void>}
     */
    async _sendForm(formData) {
      const action = this.elements.form.getAttribute('action') || '/contact';
      // Shopify's contact endpoint expects application/x-www-form-urlencoded,
      // not the multipart/form-data that FormData defaults to.
      const body = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        body.append(key, value);
      }

      try {
        const response = await fetch(action, {
          method: 'POST',
          body,
        });

        if (response.ok) {
          const values = Object.fromEntries(formData.entries());
          this.handleSuccess(values);
        } else {
          console.error('Form submission failed:', response.status, response.statusText);
          this._handleError({ message: 'An error occurred. Please try again.' });
        }
      } catch (error) {
        console.error('Form submission error:', error);
        this._handleError({ message: 'Network error. Please check your connection and try again.' });
      }
    }

    /**
     * Handle successful form submission
     * @returns {void}
     */
    handleSuccess(values = {}) {
      this.classList.remove(this.classes.loading);
      this.classList.add(this.classes.success);

      // Notify children (e.g. wlsn-form-contact-thanks) with submitted values
      // BEFORE revealing the success message, so tokens are hydrated first.
      this.dispatchEvent(
        new CustomEvent('wlsn-form:success', {
          detail: { values },
          bubbles: true,
        })
      );

      // Hide form fields
      if (this.elements.formFields) {
        this.elements.formFields.classList.add(this.classes.hidden);
      }

      // Show success message
      if (this.elements.successMessage) {
        this.elements.successMessage.classList.remove(this.classes.hidden);
        this.elements.successMessage.setAttribute('tabindex', '-1');
        this.elements.successMessage.focus();
      }

      // Reset form
      if (this.elements.form) {
        this.elements.form.reset();
      }

      // Re-enable submit button
      if (this.elements.submitButton) {
        this.elements.submitButton.disabled = false;
        this.elements.submitButton.removeAttribute('aria-busy');
      }

      // Auto-revert to form after configured delay (opt-in via data attribute)
      const autoResetDelay = parseInt(this.getAttribute('data-success-reset-delay'), 10);
      if (autoResetDelay > 0) {
        this._scheduleSuccessReset(autoResetDelay);
      }
    }

    /**
     * Revert from success state back to the form after a delay.
     * Used by the contact form to keep the success message visible briefly,
     * then make the form ready for another submission.
     * @param {number} delay - Delay in ms
     * @returns {void}
     */
    _scheduleSuccessReset(delay) {
      setTimeout(() => {
        // Hide success message
        if (this.elements.successMessage) {
          this.elements.successMessage.classList.add(this.classes.hidden);
          this.elements.successMessage.removeAttribute('tabindex');
        }

        // Show form fields
        if (this.elements.formFields) {
          this.elements.formFields.classList.remove(this.classes.hidden);
        }

        // Contact form: also reveal its headline (hidden alongside the fields on success).
        this.querySelectorAll('.wlsn-form-contact__headline').forEach((el) => {
          el.classList.remove(this.classes.hidden);
        });

        // Clear success state class and reset form values
        this.classList.remove(this.classes.success);
        if (this.elements.form) {
          this.elements.form.reset();
        }
      }, delay);
    }

    /**
     * Handle form submission error
     * @param {object} error - Error object with message
     * @returns {void}
     */
    _handleError(error) {
      this.classList.remove(this.classes.loading);
      this.classList.add(this.classes.error);

      // Re-enable submit button
      if (this.elements.submitButton) {
        this.elements.submitButton.disabled = false;
        this.elements.submitButton.removeAttribute('aria-busy');
      }

      // Show general error message
      alert(error.message || 'An error occurred. Please try again.');
    }
  }

  customElements.define('wlsn-form', WLSNForm);
}
